import { useEffect, useRef } from "react";
import styled from "styled-components";
import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import DecorativeElements from "./DecorativeElements/DecorativeElements";

/* Tabs are rendered in-flow by the pages themselves (typically right
   below CollectionFilter). The setSearchValue / isFeedView /
   onToggleViewMode / showViewModeToggle props are kept for back-compat
   with existing callers but ignored here. */

const MainContainer = ({
  children,
  offPadding,
  hideTabs = true,
  // eslint-disable-next-line no-unused-vars
  setSearchValue,
  fullWidth,
  hideOverflowX,
  // eslint-disable-next-line no-unused-vars
  isFeedView = false,
  // eslint-disable-next-line no-unused-vars
  onToggleViewMode,
  // eslint-disable-next-line no-unused-vars
  showViewModeToggle = false,
}) => {
  const alreadyGeneratedVideo = localStorage.getItem("alreadyGeneratedVideo");
  const backgroundVideoRef = useRef(null);

  useEffect(() => {
    const resume = () => {
      const el = backgroundVideoRef.current;
      if (!el || document.hidden) return;
      const promise = el.play();
      if (promise && typeof promise.catch === "function") {
        promise.catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);

    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
    };
  }, []);

  return (
    <Container>
      <Header />
      <DecorativeElements />
      <Content
        $offPadding={offPadding}
        $hideTabs={hideTabs}
        $fullWidth={fullWidth}
        $alreadyGeneratedVideo={alreadyGeneratedVideo}
        $hideOverflowX={hideOverflowX}
      >
        {children}
        <Footer isFeedView={isFeedView} />
      </Content>
    </Container>
  );
};

export default MainContainer;

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  min-height: 100dvh;
  /* Soft aqua wash: pale blue-white with radial aqua highlights.
     Note: body has the same gradient set; this one was effectively a
     duplicate. Kept here in case the body bg ever scrolls past, but
     attachment is the default (scroll) — fixed forces a full repaint
     on every scroll frame, doubling the cost when body bg also fixes. */
  background:
    radial-gradient(
      circle at 12% 16%,
      rgba(var(--color-brand-300-rgb), var(--bg-radial-a-alpha)),
      transparent 0 32%
    ),
    radial-gradient(
      circle at 88% 10%,
      rgba(var(--color-brand-400-rgb), var(--bg-radial-b-alpha)),
      transparent 0 28%
    ),
    radial-gradient(
      circle at 50% 92%,
      rgba(var(--color-brand-200-rgb), var(--bg-radial-c-alpha)),
      transparent 0 34%
    ),
    var(--glass-page-grad);

  color: var(--color-text-strong);
  width: 100%;
  isolation: isolate;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
`;

const Content = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  height: 100%;
  min-height: 100dvh;

  color: var(--color-text-strong);
  transition: color 0.3s ease;
  max-width: ${(props) =>
    props.$fullWidth ? "unset" : "min(1680px, calc(100vw - 48px))"};
  margin: 0 auto;
  /* Tabs are in-flow now — only the Header height is reserved here.
     offPadding still wins for full-bleed pages. */
  padding-top: ${(p) => (p.$offPadding ? "0px" : "64px")};

  gap: 16px;
  overflow-x: ${(p) => (p.$hideOverflowX ? "hidden" : "unset")};
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 748px) {
    padding-top: ${(p) => (p.$offPadding ? "0px" : "74px")};
  }
`;
